import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useResponsive';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Popover,
  TablePagination,
  InputAdornment,
  TextField,
  Select,
  SelectChangeEvent,
  MenuItem,
  Divider,
  Stack,
  Paper,
  Checkbox,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  SuccessResponse,
  Class,
  ClassCoach,
  ClassStudent,
  ClassStudentPackage,
  PaginatedResponse,
  Response,
  CourseLevel,
  CheckInSummary,
} from '../../types/ClassTable.type';
import { apiDelete, apiGet, apiPost } from '../../utils/apiClient';
import {
  PersonRemove,
  Search,
  CheckCircle,
  Cancel,
  Groups,
  HowToReg,
  Done,
  Visibility,
} from '@mui/icons-material';
import {
  serviceColors,
  serviceTypeColors,
  userTypeColors,
} from '../../utils/colorHandler';
import AddCoachDialog from '../Class/AddCoachDialog';
import { notifyAPIClient } from '../../utils/toast';
import AddStudentDialog from '../Class/AddStudentDialog';
import AttendanceDialog from './AttendanceDialog';
import { TableNoData, TableSpacerRow } from '../common/TableEmptyState';
import TableLoader from '../common/TableLoader';
import ColorChip from '../common/ColorChip';
import SofLevelChip from '../common/SofLevelChip';
import { EmptyValue } from '../common/EmptyValue';
import { PlusCircle } from 'lucide-react';
import {
  MANAGEMENT_TABLE_HEADER_COLOR,
  ManagementTableActionCell,
  ManagementTableHeaderCell,
  managementTableRowSx,
} from '../common/ManagementTable';

type ClassMembersProps = {
  open: boolean;
  handleClose: () => void;
  record: Class | null;
};

const checkInStatusColors: Record<string, string> = {
  CHECKED_IN: '#11734b',
  CHECKED_IN_BG: '#d4edbc',
  NOT_CHECKED_IN: '#bc1e1d',
  NOT_CHECKED_IN_BG: '#ffcfc9',
};

const formatRegistrationDate = (value?: string): string => {
  if (!value) return '---';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
};

const isRegistrationExpired = (value?: string): boolean => {
  if (!value) return false;
  const expiryDate = new Date(value);
  return !Number.isNaN(expiryDate.getTime()) && expiryDate < new Date();
};

const getCoursePackages = (student: ClassStudent): ClassStudentPackage[] => {
  if (student.registration.packages?.length) {
    return student.registration.packages;
  }

  return [
    {
      id: student.registration.id,
      course: student.registration.subsubtype.name,
      registrationStartDate: student.registration.sos || '',
      registrationEndDate: student.registration.eos || '',
    },
  ];
};

const ClassMembersDialog = ({
  open,
  handleClose,
  record,
}: ClassMembersProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Jump from this dialog to the full class-management screen. The two screens
  // are role-split, so route by the signed-in user's role: ADMIN -> /class,
  // COACH -> /coach-class.
  const handleGoToClassScreen = () => {
    const user = JSON.parse(
      localStorage.getItem('aquatics_hub_2025_user') || 'null',
    );
    const target = user?.role === 'COACH' ? '/coach-class' : '/class';
    handleCloseDialog();
    navigate(target);
  };

  const [classCoaches, setClassCoaches] = React.useState<ClassCoach[]>([]);
  const [classStudents, setClassStudents] = React.useState<ClassStudent[]>([]);
  const [studentsLoading, setStudentsLoading] = React.useState(false);
  const [checkInSummary, setCheckInSummary] =
    React.useState<CheckInSummary | null>(null);
  const [checkInStudent, setCheckInStudent] =
    React.useState<ClassStudent | null>(null);
  const [courseStudent, setCourseStudent] =
    React.useState<ClassStudent | null>(null);
  const [checkInSubmitting, setCheckInSubmitting] = React.useState(false);
  const [openAttendanceDialog, setOpenAttendanceDialog] =
    React.useState(false);
  const [attendanceRegistrationId, setAttendanceRegistrationId] =
    React.useState<number | null>(null);

  // Pagination
  const [coachPage, setCoachPage] = React.useState(1);
  const [studentPage, setStudentPage] = React.useState(1);

  const [coachLimit, setCoachLimit] = React.useState(10);
  const [studentLimit, setStudentLimit] = React.useState(10);

  const [coachTotalItems, setCoachTotalItems] = React.useState(0);
  const [studentTotalItems, setStudentTotalItems] = React.useState(0);

  const [coachSearchTerm, setCoachSearchTerm] = React.useState('');
  const [studentSearchTerm, setStudentSearchTerm] = React.useState('');

  const [levelOptions, setLevelOptions] = React.useState<CourseLevel[]>([]);
  const [levelFilter, setLevelFilter] = React.useState<CourseLevel>({
    level: -1,
    levelName: 'All',
  });
  const [isCheckInToday, setIsCheckInToday] = React.useState<string>('all');

  // Bulk check-in: selected student ids (keyed by ClassStudent.id, matching the
  // row key and the check-in summary lookup).
  const [selectedStudentIds, setSelectedStudentIds] = React.useState<
    Set<number>
  >(new Set());
  const [bulkCheckInSubmitting, setBulkCheckInSubmitting] =
    React.useState(false);

  // Dialog states
  const [openAddCoachDialog, setOpenAddCoachDialog] = React.useState(false);
  const [openAddStudentDialog, setOpenAddStudentDialog] = React.useState(false);

  const [highlightedCoachRow, setHighlightedCoachRow] = React.useState<
    string | null
  >(null);
  const [highlightedStudentRow, setHighlightedStudentRow] = React.useState<
    string | null
  >(null);

  // Handle logic
  const handleChangeCoachPage = (_: unknown, newPage: number) => {
    setCoachPage(newPage + 1);
  };

  const handleChangeStudentPage = (_: unknown, newPage: number) => {
    setStudentPage(newPage + 1);
  };

  const handleChangeCoachRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCoachLimit(parseInt(event.target.value, 10));
    setCoachPage(1);
  };

  const handleChangeStudentRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setStudentLimit(parseInt(event.target.value, 10));
    setStudentPage(1);
  };

  const handleSearchChange = (actor = 'COACH', value: string) => {
    if (actor === 'COACH') {
      setCoachSearchTerm(value);
      setCoachPage(1);
    } else {
      setStudentSearchTerm(value);
      setStudentPage(1);
    }
  };

  const fetchCheckInSummary = async () => {
    if (record) {
      try {
        const response = await apiGet<SuccessResponse>(
          `/class/check-in-summary/${record.id}`,
        );
        setCheckInSummary(response.data);
      } catch (error) {
        console.error('Error fetching check-in summary:', error);
      }
    }
  };

  const fetchCoaches = async () => {
    if (record) {
      try {
        const response = await apiGet<PaginatedResponse<ClassCoach>>(
          `/class/coach/list/${record.id}?page=${coachPage}&limit=${coachLimit}&search=${coachSearchTerm}`,
        );
        setClassCoaches(response.data);
        setCoachTotalItems(response.metadata.totalItems);
      } catch (error) {
        console.error('Error fetching coaches:', error);
      }
    }
  };

  const fetchStudents = async () => {
    if (record) {
      try {
        setStudentsLoading(true);
        const response = await apiGet<PaginatedResponse<ClassStudent>>(
          `/class/student/list/${record.id}?page=${studentPage}&limit=${studentLimit}&search=${studentSearchTerm}&level=${levelFilter.levelName}&checkInToday=${isCheckInToday}`,
        );
        setClassStudents(response.data);
        setStudentTotalItems(response.metadata.totalItems);
      } catch (error) {
        console.error('Error fetching students:', error);
      } finally {
        setStudentsLoading(false);
      }
    }
  };

  const fetchCourseLevels = async () => {
    const res = await apiGet<Response<CourseLevel>>(
      `/course/get-levels/${record?.subtype?.subsubtypes?.[0]?.id || null}`,
    );
    setLevelOptions(res.data);
  };

  const handleFilterLevelChange = (event: SelectChangeEvent<string>) => {
    const newValue = event.target.value;
    const selectedLevel = levelOptions.find(
      (option) => option.levelName === newValue,
    ) || {
      level: -1,
      levelName: 'All',
    };

    setLevelFilter(selectedLevel);
    setStudentPage(1);
  };

  const handleCloseDialog = () => {
    handleClose();
    setClassCoaches([]);
    setClassStudents([]);
    setCheckInSummary(null);
    setCheckInStudent(null);
    setCourseStudent(null);
    setOpenAttendanceDialog(false);
    setAttendanceRegistrationId(null);
    setCoachPage(1);
    setStudentPage(1);
    setCoachLimit(10);
    setStudentLimit(10);
    setCoachTotalItems(0);
    setStudentTotalItems(0);
    setCoachSearchTerm('');
    setStudentSearchTerm('');
    setLevelOptions([]);
    setLevelFilter({
      level: -1,
      levelName: 'All',
    });
    setIsCheckInToday('all');
    setHighlightedCoachRow(null);
    setHighlightedStudentRow(null);
    setSelectedStudentIds(new Set());
  };

  const handleCloseAddCoachDialog = () => {
    setOpenAddCoachDialog(false);
    setHighlightedCoachRow(null);
  };

  const handleCloseAddStudentDialog = () => {
    setOpenAddStudentDialog(false);
    setHighlightedStudentRow(null);
  };

  const handleConfirmRemoveCoach = async (id: number | null) => {
    if (id !== null) {
      try {
        const response = await apiDelete<SuccessResponse>(
          `/class/coach/remove/${id}`,
        );

        notifyAPIClient(response.message);
        fetchCoaches();
      } catch (error) {
        console.error('Failed to remove coach:', error);
        notifyAPIClient('Failed to remove coach');
      }
    }
    setHighlightedCoachRow(null);
  };

  const handleConfirmRemoveStudent = async (id: number | null) => {
    if (id !== null) {
      try {
        const response = await apiDelete<SuccessResponse>(
          `/class/student/remove/${id}`,
        );

        notifyAPIClient(response.message);
        fetchStudents();
        fetchCheckInSummary();
      } catch (error) {
        console.error('Failed to remove student:', error);
        notifyAPIClient('Failed to remove student');
      }
    }
    setHighlightedStudentRow(null);
  };

  const handleOpenAttendanceHistory = (event: React.MouseEvent, student: ClassStudent) => {
    event.stopPropagation();
    setAttendanceRegistrationId(student.registrationId);
    setOpenAttendanceDialog(true);
  };

  const handleOpenCheckIn = (event: React.MouseEvent, student: ClassStudent) => {
    event.stopPropagation();
    setCheckInStudent(student);
  };

  const handleOpenCourse = (event: React.MouseEvent, student: ClassStudent) => {
    event.stopPropagation();
    setCourseStudent(student);
  };

  const handleConfirmCheckIn = async () => {
    if (!checkInStudent) return;

    try {
      setCheckInSubmitting(true);
      const response = await apiPost<SuccessResponse>('/attendance/create', {
        registrationId: checkInStudent.registrationId,
        attendanceDate: new Date().toISOString(),
        note: '',
      });
      notifyAPIClient(response.message || 'Check in successfully!');
      setCheckInStudent(null);
      await Promise.all([fetchStudents(), fetchCheckInSummary()]);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      notifyAPIClient(message || 'Unable to check in this student.');
    } finally {
      setCheckInSubmitting(false);
    }
  };

  // A student is checked in today when the summary says so.
  const isStudentCheckedInToday = (student: ClassStudent) =>
    checkInSummary?.summary.find((summary) => summary.id === student.id)
      ?.isCheckInToday === true;

  // Only students not yet checked in today can be bulk-selected.
  const checkableStudents = classStudents.filter(
    (student) => !isStudentCheckedInToday(student),
  );
  const allCheckableSelected =
    checkableStudents.length > 0 &&
    checkableStudents.every((student) => selectedStudentIds.has(student.id));
  const someCheckableSelected = checkableStudents.some((student) =>
    selectedStudentIds.has(student.id),
  );

  const handleToggleStudentSelection = (id: number, checked: boolean) => {
    setSelectedStudentIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAllStudents = (checked: boolean) => {
    setSelectedStudentIds(
      checked ? new Set(checkableStudents.map((student) => student.id)) : new Set(),
    );
  };

  const handleBulkCheckIn = async () => {
    // Guard against stale selections (e.g. someone got checked in individually).
    const studentsToCheckIn = classStudents.filter(
      (student) =>
        selectedStudentIds.has(student.id) && !isStudentCheckedInToday(student),
    );

    if (studentsToCheckIn.length === 0) {
      setSelectedStudentIds(new Set());
      return;
    }

    try {
      setBulkCheckInSubmitting(true);
      const results = await Promise.allSettled(
        studentsToCheckIn.map((student) =>
          apiPost<SuccessResponse>('/attendance/create', {
            registrationId: student.registrationId,
            attendanceDate: new Date().toISOString(),
            note: '',
          }),
        ),
      );
      const successCount = results.filter(
        (result) => result.status === 'fulfilled',
      ).length;
      const failedCount = results.length - successCount;

      notifyAPIClient(
        `${successCount} student(s) checked in successfully${
          failedCount > 0 ? `; ${failedCount} failed` : ''
        }.`,
      );
      setSelectedStudentIds(new Set());
      await Promise.all([fetchStudents(), fetchCheckInSummary()]);
    } finally {
      setBulkCheckInSubmitting(false);
    }
  };

  const handleUndoAttendance = async (attendanceId: number) => {
    try {
      const response = await apiPost<SuccessResponse>('/attendance/undo', {
        attendanceId,
      });
      notifyAPIClient(response.message);
      await Promise.all([fetchStudents(), fetchCheckInSummary()]);
    } catch (error) {
      const message = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      notifyAPIClient(message || 'Unable to undo check-in.');
    }
  };

  React.useEffect(() => {
    if (open) {
      fetchCoaches();
      fetchCourseLevels();
    }
  }, [open, coachPage, coachLimit]);

  React.useEffect(() => {
    if (open) fetchStudents();
  }, [open, studentPage, studentLimit, levelFilter, isCheckInToday]);

  // Clear the bulk selection whenever the visible student page/filter changes,
  // so a checkbox can't stay selected for a row that's no longer shown.
  React.useEffect(() => {
    setSelectedStudentIds(new Set());
  }, [studentPage, studentLimit, levelFilter, isCheckInToday, studentSearchTerm]);

  React.useEffect(() => {
    if (open) fetchCheckInSummary();
  }, [open, record]);

  const studentEmptyRows =
    studentPage * studentLimit >= studentTotalItems
      ? Math.max(0, studentLimit - classStudents.length)
      : 0;
  const coachEmptyRows =
    coachPage * coachLimit >= coachTotalItems
      ? Math.max(0, coachLimit - classCoaches.length)
      : 0;

  return (
    <>
      <Dialog
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
      open={open}
      onClose={handleCloseDialog}
      slotProps={{
        paper: {
          sx: {
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        },
      }}
    >
      {/* Header with gradient */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: '#fff',
          py: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="700">
            Manage Class Members
          </Typography>
          {record?.name ? (
            <Tooltip title="Go to the class management screen">
              <Typography
                variant="body2"
                component="button"
                type="button"
                onClick={handleGoToClassScreen}
                sx={{
                  opacity: 0.9,
                  mt: 0.5,
                  p: 0,
                  border: 'none',
                  background: 'none',
                  color: 'inherit',
                  font: 'inherit',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  '&:hover': { opacity: 1 },
                }}
              >
                {record.name}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Class Details
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{ mt: 3, py: 3, px: 4, overflowY: 'auto', maxHeight: '75vh' }}
      >
        {/* START - Check-in Summary Section */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <CheckCircle
              sx={{ color: MANAGEMENT_TABLE_HEADER_COLOR, fontSize: 24 }}
            />
            <Typography
              variant="h6"
              fontWeight="700"
              color={MANAGEMENT_TABLE_HEADER_COLOR}
            >
              Check-in Summary
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 3,
            }}
          >
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={(theme) => ({
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  })}
                >
                  <Groups sx={{ fontSize: 38 }} />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="secondary.main"
                >
                  Total Students
                </Typography>
                <Typography variant="h4" fontWeight="700" color="primary.main">
                  {checkInSummary?.totalStudents || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Students enrolled in this class
                </Typography>
              </Box>
            </Card>

            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={(theme) => ({
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: alpha(theme.palette.success.main, 0.12),
                    color: theme.palette.success.main,
                  })}
                >
                  <HowToReg sx={{ fontSize: 38 }} />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color="secondary.main"
                >
                  Checked In Today
                </Typography>
                <Typography variant="h4" fontWeight="700" color="success.main">
                  {checkInSummary?.totalCheckedInStudents || 0} /{' '}
                  {checkInSummary?.totalStudents || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Attendance recorded today
                </Typography>
              </Box>
            </Card>
          </Box>
        </Box>
        {/* END - Check-in Summary Section */}

        {/* START - Students Section */}
        <Box display="flex" alignItems="center" mb={2} mt={3}>
          <Typography variant="h6" fontWeight="700" color="text.secondary">
            Students List
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          gap={2}
        >
          <Box display="flex" gap={2} flex={1}>
            <TextField
              size="small"
              placeholder="Search by student name or ID"
              value={studentSearchTerm}
              onChange={(event) =>
                handleSearchChange('STUDENT', event.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  fetchStudents();
                  setStudentPage(1);
                }
              }}
              sx={{ width: 350 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Select
              size="small"
              displayEmpty
              value={levelFilter?.levelName ?? ''}
              onChange={handleFilterLevelChange}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="All">All levels</MenuItem>
              {levelOptions?.map((option: CourseLevel, index: number) => (
                <MenuItem key={index} value={option.levelName}>
                  {option.levelName}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              displayEmpty
              value={isCheckInToday}
              onChange={(event: SelectChangeEvent<string>) => {
                setIsCheckInToday(event.target.value);
                setStudentPage(1);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All status</MenuItem>
              <MenuItem value="true">Checked In Today</MenuItem>
              <MenuItem value="false">Not Checked In Today</MenuItem>
            </Select>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Bulk check-in for the selected (not-yet-checked-in) students */}
            <Button
              variant="contained"
              color="success"
              startIcon={<HowToReg />}
              disabled={
                selectedStudentIds.size === 0 || bulkCheckInSubmitting
              }
              onClick={() => void handleBulkCheckIn()}
              sx={{ color: 'white', whiteSpace: 'nowrap' }}
            >
              Check in selected
              {selectedStudentIds.size > 0
                ? ` (${selectedStudentIds.size})`
                : ''}
            </Button>

            {/* Add Student Button */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlusCircle size={18} />}
              onClick={() => {
                setOpenAddStudentDialog(true);
                setHighlightedStudentRow(null);
              }}
              sx={{ color: 'white' }}
            >
              Add Student
            </Button>
          </Stack>
        </Box>

        <TableContainer>
          <Table size="small" padding="checkbox">
            <TableHead>
              <TableRow sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}>
                <TableCell
                  padding="checkbox"
                  sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}
                >
                  <Checkbox
                    size="small"
                    checked={allCheckableSelected}
                    indeterminate={
                      someCheckableSelected && !allCheckableSelected
                    }
                    disabled={
                      checkableStudents.length === 0 || bulkCheckInSubmitting
                    }
                    onChange={(event) =>
                      handleSelectAllStudents(event.target.checked)
                    }
                    inputProps={{ 'aria-label': 'Select all students' }}
                    sx={{
                      color: '#fff',
                      '&.Mui-checked': { color: '#fff' },
                      '&.MuiCheckbox-indeterminate': { color: '#fff' },
                    }}
                  />
                </TableCell>
                <ManagementTableHeaderCell>User ID</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>Last Name</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  First Name
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  Preferred Name
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  Date of Birth
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell>Level</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>User Type</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>Program</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>Type</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>Course</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  Joined Date
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  Checked In Today
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell sticky>
                  Actions
                </ManagementTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {studentsLoading ? (
                <TableLoader colSpan={14} />
              ) : (
                <>
              {classStudents.map((student) => (
                <TableRow
                  key={student.id}
                  sx={{
                    ...managementTableRowSx(
                      highlightedStudentRow === `data_${student.id}`,
                    ),
                    ...(isRegistrationExpired(student.registration.eos)
                      ? {
                          backgroundColor: '#FFE2B8',
                          '&:hover': { backgroundColor: '#FFE2B8' },
                        }
                      : {}),
                  }}
                  onClick={() => {
                    if (highlightedStudentRow === `data_${student.id}`) {
                      setHighlightedStudentRow(null);
                    } else {
                      setHighlightedStudentRow(`data_${student.id}`);
                    }
                  }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={selectedStudentIds.has(student.id)}
                      disabled={
                        isStudentCheckedInToday(student) || bulkCheckInSubmitting
                      }
                      onChange={(event) =>
                        handleToggleStudentSelection(
                          student.id,
                          event.target.checked,
                        )
                      }
                      inputProps={{
                        'aria-label': `Select ${student.registration.user.firstName} ${student.registration.user.lastName}`,
                      }}
                    />
                  </TableCell>
                  <TableCell>{student.registration.userId}</TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                  >{`${student.registration.user.lastName}`}</TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap' }}
                  >{`${student.registration.user.firstName}`}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {student.registration.user.preferredName || <EmptyValue />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {student.registration.user.userType === 'BUSINESS'
                      ? '---'
                      : new Date(
                          student.registration.user.dob,
                        ).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <SofLevelChip
                      level={
                        student.registration.serviceType === 'COURSE'
                          ? student.registration.courseLevel
                          : null
                      }
                      serviceType={student.registration.subtype}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip
                      label={student.registration.user.userType}
                      palette={userTypeColors}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip
                      label={student.registration.serviceType}
                      palette={serviceColors}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ColorChip
                      label={student.registration.subtype}
                      palette={serviceTypeColors}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <ButtonBase
                      onClick={(event) => handleOpenCourse(event, student)}
                      aria-label={`View course information for ${student.registration.user.firstName} ${student.registration.user.lastName}`}
                      sx={{
                        minWidth: 0,
                        p: 0,
                        borderRadius: 0,
                        justifyContent: 'flex-start',
                        color: 'inherit',
                        font: 'inherit',
                        '&:hover': {
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                        },
                        '&:hover .course-text': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      <Typography
                        component="span"
                        className="course-text"
                        variant="body2"
                        sx={{ color: 'inherit', textAlign: 'left' }}
                      >
                        {student.registration.subsubtype.name || <EmptyValue />}
                      </Typography>
                    </ButtonBase>
                  </TableCell>
                  <TableCell>
                    {new Date(student.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const isCheckedIn =
                        checkInSummary?.summary.find((s) => s.id === student.id)
                          ?.isCheckInToday === true;
                      return (
                        <ColorChip
                          label={isCheckedIn ? 'Checked In' : 'Not Checked In'}
                          palette={checkInStatusColors}
                          paletteKey={
                            isCheckedIn ? 'CHECKED_IN' : 'NOT_CHECKED_IN'
                          }
                          icon={
                            isCheckedIn ? (
                              <CheckCircle sx={{ fontSize: 16 }} />
                            ) : (
                              <Cancel sx={{ fontSize: 16 }} />
                            )
                          }
                        />
                      );
                    })()}
                  </TableCell>
                  <ManagementTableActionCell
                    selected={highlightedStudentRow === `data_${student.id}`}
                  >
                    <Tooltip title="View check-in history">
                      <IconButton
                        color="primary"
                        aria-label={`View check-in history for ${student.registration.user.firstName}`}
                        onClick={(event) =>
                          handleOpenAttendanceHistory(event, student)
                        }
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        checkInSummary?.summary.find(
                          (summary) => summary.id === student.id,
                        )?.isCheckInToday
                          ? 'Already checked in today'
                          : 'Check in'
                      }
                    >
                      <span>
                        <IconButton
                          color="primary"
                          aria-label={`Check in ${student.registration.user.firstName}`}
                          disabled={
                            checkInSummary?.summary.find(
                              (summary) => summary.id === student.id,
                            )?.isCheckInToday === true
                          }
                          onClick={(event) => handleOpenCheckIn(event, student)}
                        >
                          <Done />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <ConfirmationPopover
                      classCoachId={null}
                      classStudentId={student.id}
                      handleConfirm={handleConfirmRemoveStudent}
                    />
                  </ManagementTableActionCell>
                </TableRow>
              ))}
              {classStudents.length === 0 ? (
                <TableNoData message="No students found" />
              ) : (
                <TableSpacerRow rows={studentEmptyRows} />
              )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end">
          <TablePagination
            component="div"
            count={studentTotalItems}
            page={studentPage - 1}
            onPageChange={handleChangeStudentPage}
            rowsPerPage={studentLimit}
            onRowsPerPageChange={handleChangeStudentRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </Box>
        {/* END - Students Section */}

        {/* START - Coaches Section */}
        <Divider sx={{ my: 3 }} />
        <Box display="flex" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="700" color="text.secondary">
            Coaches List
          </Typography>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          gap={2}
        >
          <TextField
            size="small"
            placeholder="Search by coach name"
            value={coachSearchTerm}
            onChange={(event) =>
              handleSearchChange('COACH', event.target.value)
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchCoaches();
                setCoachPage(1);
              }
            }}
            sx={{ width: 350 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Add Coach Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlusCircle size={18} />}
            onClick={() => {
              setOpenAddCoachDialog(true);
              setHighlightedCoachRow(null);
            }}
            sx={{ color: 'white' }}
          >
            Add Coach
          </Button>
        </Box>
        <TableContainer>
          <Table size="small" padding="checkbox">
            <TableHead>
              <TableRow sx={{ backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR }}>
                <ManagementTableHeaderCell>Full Name</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>Email</ManagementTableHeaderCell>
                <ManagementTableHeaderCell>
                  Joined Date
                </ManagementTableHeaderCell>
                <ManagementTableHeaderCell sticky>
                  Actions
                </ManagementTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classCoaches.map((coach) => (
                <TableRow
                  key={coach.id}
                  sx={managementTableRowSx(
                    highlightedCoachRow === `data_${coach.id}`,
                  )}
                  onClick={() => {
                    if (highlightedCoachRow === `data_${coach.id}`) {
                      setHighlightedCoachRow(null);
                    } else {
                      setHighlightedCoachRow(`data_${coach.id}`);
                    }
                  }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {coach.coach.name || <EmptyValue />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {coach.coach.email || <EmptyValue />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(coach.createdAt).toLocaleDateString()}
                  </TableCell>
                  <ManagementTableActionCell
                    selected={highlightedCoachRow === `data_${coach.id}`}
                  >
                    <ConfirmationPopover
                      classCoachId={coach.id}
                      classStudentId={null}
                      handleConfirm={handleConfirmRemoveCoach}
                    />
                  </ManagementTableActionCell>
                </TableRow>
              ))}
              {classCoaches.length === 0 ? (
                <TableNoData message="No coaches found" />
              ) : (
                <TableSpacerRow rows={coachEmptyRows} />
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="flex-end">
          <TablePagination
            component="div"
            count={coachTotalItems}
            page={coachPage - 1}
            onPageChange={handleChangeCoachPage}
            rowsPerPage={coachLimit}
            onRowsPerPageChange={handleChangeCoachRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </Box>
        {/* END - Coaches Section */}

        {/* Add Coach Dialog */}
        <AddCoachDialog
          open={openAddCoachDialog}
          handleClose={handleCloseAddCoachDialog}
          selectedClass={record}
          fetchCoaches={fetchCoaches}
        />

        {/* Add Student Dialog */}
        <AddStudentDialog
          open={openAddStudentDialog}
          handleClose={handleCloseAddStudentDialog}
          selectedClass={record}
          fetchStudents={fetchStudents}
          fetchCheckInSummary={fetchCheckInSummary}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleCloseDialog}
          sx={{
            px: 3,
            py: 1,
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Close
        </Button>
      </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(courseStudent)}
        onClose={() => setCourseStudent(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Course Information</DialogTitle>
        <DialogContent dividers>
          {courseStudent && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Student
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {courseStudent.registration.user.firstName}{' '}
                  {courseStudent.registration.user.lastName}
                </Typography>
              </Box>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small" aria-label="Course registration period">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: MANAGEMENT_TABLE_HEADER_COLOR,
                      }}
                    >
                      <ManagementTableHeaderCell>
                        Course
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Registration Start Date
                      </ManagementTableHeaderCell>
                      <ManagementTableHeaderCell>
                        Expiry Date
                      </ManagementTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getCoursePackages(courseStudent).map((coursePackage) => (
                      <TableRow key={coursePackage.id}>
                        <TableCell>{coursePackage.course}</TableCell>
                        <TableCell>
                          {formatRegistrationDate(
                            coursePackage.registrationStartDate,
                          )}
                        </TableCell>
                        <TableCell>
                          {formatRegistrationDate(
                            coursePackage.registrationEndDate,
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCourseStudent(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(checkInStudent)}
        onClose={() => !checkInSubmitting && setCheckInStudent(null)}
      >
        <DialogTitle>Confirm Checkin</DialogTitle>
        <DialogContent>Are you sure to checkin for this user?</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCheckInStudent(null)}
            disabled={checkInSubmitting}
          >
            Close
          </Button>
          <Button
            onClick={() => void handleConfirmCheckIn()}
            disabled={checkInSubmitting}
          >
            {checkInSubmitting ? 'Checking...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <AttendanceDialog
        open={openAttendanceDialog}
        handleClose={() => {
          setOpenAttendanceDialog(false);
          setAttendanceRegistrationId(null);
        }}
        registrationId={attendanceRegistrationId}
        handleUndoAttendance={handleUndoAttendance}
      />
    </>
  );
};

export default ClassMembersDialog;

const ConfirmationPopover = ({
  classCoachId,
  classStudentId,
  handleConfirm,
}: {
  classCoachId: number | null;
  classStudentId: number | null;
  handleConfirm: (id: number | null) => void;
}) => {
  const [openConfirmation, setOpenConfirmation] =
    React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenConfirmation(event.currentTarget);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(null);
  };

  const openPopover = Boolean(openConfirmation);
  const id = openPopover ? 'confirm-popover' : undefined;

  return (
    <>
      <Tooltip
        title={`${classCoachId ? 'Remove coach from class' : 'Remove student from class'}`}
      >
        <IconButton
          color="error"
          aria-label="remove"
          aria-describedby={id}
          onClick={handleClick}
        >
          <PersonRemove />
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={openPopover}
        anchorEl={openConfirmation}
        onClose={handleCloseConfirmation}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Typography sx={{ p: 1, fontSize: '0.9rem' }}>
          {`Are you sure you want to remove this ${
            classCoachId ? 'coach' : 'student'
          } from the class?`}
        </Typography>
        <Box display={'flex'} gap={1} px={1} pb={1} justifyContent={'end'}>
          <Button
            onClick={handleCloseConfirmation}
            variant="contained"
            color="error"
            sx={{ fontSize: '0.7rem' }}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              handleConfirm(classCoachId || classStudentId);
              handleCloseConfirmation();
            }}
            variant="contained"
            color="primary"
            className="!text-white"
            sx={{ fontSize: '0.7rem' }}
          >
            Confirm
          </Button>
        </Box>
      </Popover>
    </>
  );
};
